import React, { useState, useEffect } from 'react';
import './ReportToolManagement.css';
import { supabase } from '../supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const ADMIN_ORG_ID = '6181f59e-5307-4b81-adee-f720d48fe47e';

const ReportToolManagement = () => {
    const [sessions, setSessions] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingStatus, setSavingStatus] = useState({}); // Track saving status by session_id

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch sessions created by the admin organization
            const { data: sessionsData, error: sessionsError } = await supabase
                .from('sessions')
                .select('*')
                .eq('org_id', ADMIN_ORG_ID)
                .order('created_at', { ascending: false });

            if (sessionsError) throw sessionsError;

            // 2. Fetch all organizations (to populate dropdowns and find current assignments)
            // Use report_session_permission instead of review_session_permission
            const { data: orgsData, error: orgsError } = await supabase
                .from('organizations')
                .select('org_id, org_name, report_session_permission');

            if (orgsError) throw orgsError;

            setOrganizations(orgsData || []);
            setSessions(sessionsData || []);

        } catch (error) {
            console.error('Error fetching data:', error.message);
            alert('Failed to load report tool data.');
        } finally {
            setLoading(false);
        }
    };

    // Calculate current assignment for a session based on org permissions
    const getAssignedOrgId = (sessionId) => {
        const assignedOrg = organizations.find(org => {
            const permissions = org.report_session_permission || [];
            return permissions.includes(sessionId);
        });
        return assignedOrg ? assignedOrg.org_id : '';
    };

    const handleAssignChange = async (sessionId, newOrgId) => {
        setSavingStatus(prev => ({ ...prev, [sessionId]: 'saving' }));
        try {
            const currentOrgId = getAssignedOrgId(sessionId);
            
            // If they are the same, do nothing
            if (currentOrgId === newOrgId) {
                setSavingStatus(prev => ({ ...prev, [sessionId]: null }));
                return;
            }

            const updates = [];

            // 1. Remove from the current organization if it exists
            if (currentOrgId) {
                const currentOrg = organizations.find(o => o.org_id === currentOrgId);
                const currentPermissions = currentOrg?.report_session_permission || [];
                const updatedPermissions = currentPermissions.filter(id => id !== sessionId);
                
                const { error: removeError } = await supabase
                    .from('organizations')
                    .update({ report_session_permission: updatedPermissions })
                    .eq('org_id', currentOrgId);

                if (removeError) throw removeError;
                
                // Track update for local state
                updates.push({ org_id: currentOrgId, permissions: updatedPermissions });
            }

            // 2. Add to the new organization if one is selected
            if (newOrgId) {
                const newOrg = organizations.find(o => o.org_id === newOrgId);
                const newPermissions = [...(newOrg?.report_session_permission || []), sessionId];
                // Remove duplicates just in case
                const uniqueNewPermissions = [...new Set(newPermissions)];

                const { error: addError } = await supabase
                    .from('organizations')
                    .update({ report_session_permission: uniqueNewPermissions })
                    .eq('org_id', newOrgId);

                if (addError) throw addError;

                // Track update for local state
                updates.push({ org_id: newOrgId, permissions: uniqueNewPermissions });
            }

            // Successfully updated database, apply changes locally
            setOrganizations(prevOrgs => prevOrgs.map(org => {
                const update = updates.find(u => u.org_id === org.org_id);
                if (update) {
                    return { ...org, report_session_permission: update.permissions };
                }
                return org;
            }));

            setSavingStatus(prev => ({ ...prev, [sessionId]: 'success' }));
            setTimeout(() => {
                setSavingStatus(prev => ({ ...prev, [sessionId]: null }));
            }, 3000);

        } catch (error) {
            console.error('Error updating assignment:', error.message);
            alert('Failed to update assignment. Ensure the column "report_session_permission" exists in the organizations table.');
            setSavingStatus(prev => ({ ...prev, [sessionId]: 'error' }));
        }
    };

    return (
        <div className="report-tool-container">
            <div className="report-tool-header">
                <div>
                    <h1>Report Tool Access Management</h1>
                    <p>Manage report tool assignments for sessions created by the Admin Organization.</p>
                </div>
                <button className="refresh-btn" onClick={fetchData} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> {loading ? 'Loading...' : 'Refresh Data'}
                </button>
            </div>

            <div className="report-table-wrapper">
                {loading ? (
                    <div className="loading-state">Fetching admin sessions...</div>
                ) : (
                    <table className="report-table">
                        <thead>
                            <tr>
                                <th>Session Name</th>
                                <th>Domain Name</th>
                                <th>Session Type</th>
                                <th>Created At</th>
                                <th>Assigned To Organization</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.length > 0 ? (
                                sessions.map(session => (
                                    <tr key={session.session_id}>
                                        <td><strong>{session.session_name}</strong></td>
                                        <td>{session.domain_name}</td>
                                        <td>
                                            <span className="type-badge">
                                                {session.session_type || 'default'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(session.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="assignment-cell">
                                            <select
                                                className="org-select"
                                                value={getAssignedOrgId(session.session_id)}
                                                onChange={(e) => handleAssignChange(session.session_id, e.target.value)}
                                            >
                                                <option value="">-- Unassigned --</option>
                                                {organizations
                                                    .filter(org => org.org_id !== ADMIN_ORG_ID)
                                                    .map(org => (
                                                        <option key={org.org_id} value={org.org_id}>
                                                            {org.org_name}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </td>
                                        <td className="status-cell">
                                            {savingStatus[session.session_id] === 'saving' && (
                                                <span className="status-saving">Saving...</span>
                                            )}
                                            {savingStatus[session.session_id] === 'success' && (
                                                <span className="status-success"><FontAwesomeIcon icon={faCheckCircle} /> Saved</span>
                                            )}
                                            {savingStatus[session.session_id] === 'error' && (
                                                <span className="status-error">Error</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="empty-state">
                                        No sessions found created by the Admin Organization.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ReportToolManagement;
